/**
 * RecordHeader Component - Salesforce SLDS Style
 *
 * Шапка карточки записи как в Salesforce Lightning.
 * Тёмно-синий фон (#032D60), компактные поля.
 */

import React, { useState } from 'react';
import {
  Box,
  Group,
  Text,
  Title,
  Button,
  Badge,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconArrowLeft,
  IconUserPlus,
} from '@tabler/icons-react';
import tokens from '../../../theme/designTokens';
import AssignUserModal, { AssignSuccessModal } from './AssignUserModal';

/**
 * Компонент поля в строке сводки
 */
const HeaderField = ({ label, value, isFirst = false }) => {
  if (!value) return null;

  return (
    <Group gap={0} wrap="nowrap">
      {!isFirst && (
        <Box
          mx="lg"
          style={{
            width: '1px',
            height: '32px',
            backgroundColor: 'rgba(255,255,255,0.3)',
          }}
        />
      )}
      <Box style={{ minWidth: '80px' }}>
        <Text
          size="xs"
          c="rgba(255,255,255,0.6)"
          fw={400}
          tt="uppercase"
          mb={4}
        >
          {label}
        </Text>
        <Text size="sm" c="white" fw={500}>
          {value}
        </Text>
      </Box>
    </Group>
  );
};

/**
 * Стили для кнопок в шапке (белая обводка)
 */
const outlineButtonStyles = {
  root: {
    color: 'white',
    borderColor: 'rgba(255,255,255,0.5)',
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)',
      borderColor: 'white'
    }
  }
};

/**
 * Основной компонент шапки
 */
const RecordHeader = ({ organization, currentUser, onBack, onOrganizationUpdate }) => {
  const [assignModalOpened, setAssignModalOpened] = useState(false);
  const [assignModalType, setAssignModalType] = useState(null); // 'sales' | 'manager'
  const [successModalOpened, setSuccessModalOpened] = useState(false);

  // Определяем, десктоп ли это (md breakpoint = 62em = 992px)
  const isDesktop = useMediaQuery('(min-width: 62em)');

  const ratingText = organization.rating
    ? `${organization.rating} (${organization.review_count || 0} отзывов)`
    : null;

  // Определение роли пользователя
  const userRole = currentUser?.role;
  const isSales = userRole === 'SALES';
  const isAdmin = userRole === 'ADMIN';
  const isManager = userRole === 'MANAGER';
  const canAssignManager = isAdmin || isManager;

  // Для SALES: не показываем кнопку если организация уже назначена ему
  const isAlreadyAssignedToMe = organization.assigned_to_user_id === currentUser?.id;
  const canAssignSales = (isAdmin || isManager || isSales) && !(isSales && isAlreadyAssignedToMe);

  // Открытие модального окна назначения
  const handleOpenAssignModal = (type) => {
    setAssignModalType(type);
    setAssignModalOpened(true);
  };

  // Обработка успешного назначения
  const handleAssignSuccess = (updatedOrg) => {
    if (onOrganizationUpdate) {
      onOrganizationUpdate(updatedOrg);
    }
    // Для сейла показываем модальное окно успеха
    if (isSales && assignModalType === 'sales') {
      setSuccessModalOpened(true);
    }
  };

  // Текст кнопки назначения сейла в зависимости от роли
  const getSalesButtonText = () => {
    if (isSales) {
      return 'Взять организацию';
    }
    return 'Добавить сейла';
  };

  // Есть ли кнопки для отображения
  const hasAssignButtons = canAssignManager || canAssignSales;

  return (
    <>
      <Box
        px="md"
        py="sm"
        mx="md"
        mt="md"
        style={{
          backgroundColor: tokens.colors.brand.darker, // #032D60 - тёмно-синий
          color: 'white',
          borderRadius: '16px',
          position: 'relative',
        }}
      >
        {/* Кнопки назначения - только на десктопе, абсолютное позиционирование */}
        <Box
          visibleFrom="md"
          style={{
            position: 'absolute',
            top: '12px',
            bottom: '12px',
            right: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            justifyContent: 'space-between',
          }}
        >
          {canAssignManager && (
            <Button
              variant="outline"
              size="md"
              leftSection={<IconUserPlus size={20} />}
              onClick={() => handleOpenAssignModal('manager')}
              styles={{
                ...outlineButtonStyles,
                root: {
                  ...outlineButtonStyles.root,
                  flex: 1,
                  minHeight: '40px',
                }
              }}
            >
              Добавить менеджера
            </Button>
          )}

          {canAssignSales && (
            <Button
              variant="outline"
              size="md"
              leftSection={<IconUserPlus size={20} />}
              onClick={() => handleOpenAssignModal('sales')}
              styles={{
                ...outlineButtonStyles,
                root: {
                  ...outlineButtonStyles.root,
                  flex: 1,
                  minHeight: '40px',
                }
              }}
            >
              {getSalesButtonText()}
            </Button>
          )}
        </Box>

        {/* Кнопка назад */}
        <Button
          variant="outline"
          size="compact-sm"
          leftSection={<IconArrowLeft size={16} />}
          onClick={onBack}
          mb="xs"
          styles={outlineButtonStyles}
        >
          Организации
        </Button>

        {/* Название - отступ справа только на десктопе для кнопок */}
        <Box
          mb="md"
          style={{
            paddingRight: isDesktop && hasAssignButtons ? '220px' : 0,
          }}
        >
          <Group gap="sm" align="center">
            <Title order={3} c="white" fw={700} style={{ fontSize: '1.25rem' }}>
              {organization.name}
            </Title>
            {organization.organization_type === 'b2c' && (
              <Badge color="green" variant="filled" radius="xl" size="sm">B2C</Badge>
            )}
            {organization.organization_type === 'individual' && (
              <Badge color="orange" variant="filled" radius="xl" size="sm">Инд.</Badge>
            )}
          </Group>
        </Box>

        {/* Строка полей сводки */}
        <Group gap={0} wrap="wrap" align="center">
          <HeaderField
            label="Категория"
            value={organization.category}
            isFirst={true}
          />

          <HeaderField
            label="Район"
            value={organization.district_name}
          />

          <HeaderField
            label="Рейтинг"
            value={ratingText}
          />

          <HeaderField
            label="Сейл"
            value={organization.assigned_to?.full_name || organization.assigned_to?.email || '—'}
          />

          <HeaderField
            label="Менеджер"
            value={organization.manager?.full_name || organization.manager?.email || '—'}
          />
        </Group>

        {/* Кнопки назначения - только на мобильных, внизу хедера */}
        {hasAssignButtons && (
          <Group
            hiddenFrom="md"
            mt="md"
            gap="xs"
            grow
          >
            {canAssignManager && (
              <Button
                variant="outline"
                size="sm"
                leftSection={<IconUserPlus size={16} />}
                onClick={() => handleOpenAssignModal('manager')}
                styles={outlineButtonStyles}
              >
                Менеджер
              </Button>
            )}

            {canAssignSales && (
              <Button
                variant="outline"
                size="sm"
                leftSection={<IconUserPlus size={16} />}
                onClick={() => handleOpenAssignModal('sales')}
                styles={outlineButtonStyles}
              >
                {isSales ? 'Взять' : 'Сейл'}
              </Button>
            )}
          </Group>
        )}
      </Box>

      {/* Модальное окно назначения */}
      <AssignUserModal
        opened={assignModalOpened}
        onClose={() => setAssignModalOpened(false)}
        type={assignModalType}
        organizationId={organization.id}
        currentUser={currentUser}
        onSuccess={handleAssignSuccess}
      />

      {/* Модальное окно успешного назначения (для SALES) */}
      <AssignSuccessModal
        opened={successModalOpened}
        onClose={() => setSuccessModalOpened(false)}
      />
    </>
  );
};

export default RecordHeader;
